import { z } from 'zod';

import {
  onboardingAiUsagePatternSchema,
  onboardingExperienceLevelSchema,
  onboardingGoalSchema,
  onboardingWeakAreaSchema,
} from './onboarding';
import { questionDifficultySchema, questionTypeSchema } from './question';

/**
 * PRE-73: admin question-generation contracts (LLM generation, prompt-template
 * versioning, draft → review → approve). Admin-only; consumed by the admin API
 * (`/v1/admin/*`) and the admin panel UI (PRE-74).
 */

/**
 * Why a draft was turned down (PRE-262).
 *
 * A closed list rather than free text, and that is the point of it. At 15,000
 * cells the reason a reviewer clicks is worth more in aggregate than in the
 * individual: "eleven of the last thirty rejects were `factually_wrong`" is an
 * instruction to change the prompt template, and thirty hand-typed sentences
 * are thirty sentences nobody reads. The free-text note stays available beside
 * it for the case the list does not cover.
 *
 * `judge_blocked` is the machine's own reason and no human ever picks it — it
 * marks a draft the advisory judge stopped before review, so the two can be
 * told apart when measuring whether the threshold is set right.
 */
export const rejectionReasonSchema = z.enum([
  'factually_wrong',
  'ambiguous',
  'too_easy',
  'too_hard',
  'off_skill',
  'duplicate',
  'badly_worded',
  'biased',
  'judge_blocked',
  'other',
]);
export type RejectionReason = z.infer<typeof rejectionReasonSchema>;

/** The reasons a person may choose. `judge_blocked` is the machine's alone. */
export const HUMAN_REJECTION_REASONS = rejectionReasonSchema.options.filter(
  (r) => r !== 'judge_blocked',
);

/**
 * A question's place in its life.
 *
 * The first three are the authoring path (PRE-73): `draft` has never been live,
 * `rejected` never will be, `published` is the live bank.
 *
 * `pretest` (PRE-260) is the odd one and the important one: it IS served, and
 * its answers never count toward what a learner knows. It is the low-stakes
 * slot an item sits in until real answers say how hard it actually is.
 *
 * `under_review` and `retired` (PRE-261) are the retirement flow. `under_review`
 * is deliberately not `draft` — a draft is work nobody has approved, while an
 * item under review is live work a measurement has cast doubt on, and one word
 * for both would lose which a reviewer is looking at. Nothing is ever deleted:
 * a question that was answered is part of how somebody was measured, and
 * `retired` says "stop asking this" without rewriting that.
 */
export const questionStatusSchema = z.enum([
  'draft',
  'published',
  'rejected',
  'pretest',
  'under_review',
  'retired',
]);
export type QuestionStatus = z.infer<typeof questionStatusSchema>;

/** Skill option for the admin generate screen (GET /v1/admin/skills). */
export const adminSkillSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  /**
   * PRE-92: the parent skill's slug, or null for a root. Replaces `category` —
   * the hierarchy lives in `skills` now, not in a parallel taxonomy table.
   */
  parentId: z.string().nullable().default(null),
  parentSlug: z.string().nullable().default(null),
  description: z.string().default(''),
  /** How many questions (any status) reference this skill. */
  questionCount: z.number().int().default(0),
  /**
   * PRE-154: who put the row here — `curated` (a person wrote it),
   * `stackoverflow` (the seeded catalog) or `user_generated` (a CV named it).
   * Defaulted rather than required so an older response still parses.
   */
  origin: z.enum(['curated', 'user_generated', 'stackoverflow']).default('curated'),
});
export type AdminSkill = z.infer<typeof adminSkillSchema>;

/**
 * PRE-155: a page of skills. `total` is the count before paging, so a screen can
 * say "50 of 2,482" instead of leaving a reader to wonder where the list ended.
 */
export const adminSkillPageSchema = z.object({
  items: z.array(adminSkillSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type AdminSkillPage = z.infer<typeof adminSkillPageSchema>;
export interface AdminSkillQuery {
  q?: string;
  origin?: AdminSkill['origin'];
  limit?: number;
  offset?: number;
}

/** Slug: lowercase words joined by single hyphens (BR-33 unique). */
const skillSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase words joined by hyphens');

/** Create a skill (POST /v1/admin/skills). */
export const createSkillInputSchema = z.object({
  slug: skillSlugSchema,
  title: z.string().trim().min(1).max(120),
  /** Parent skill id, or null/omitted for a root skill. */
  parentId: z.string().trim().min(1).nullable().optional(),
  description: z.string().max(2000).optional(),
});
export type CreateSkillInput = z.infer<typeof createSkillInputSchema>;

/** Update a skill (PATCH /v1/admin/skills/:id) — every field optional. */
export const updateSkillInputSchema = z
  .object({
    slug: skillSlugSchema,
    title: z.string().trim().min(1).max(120),
    parentId: z.string().trim().min(1).nullable(),
    description: z.string().max(2000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateSkillInput = z.infer<typeof updateSkillInputSchema>;

/** Rubric criterion (mirrors the seed rubric shape). */
export const adminRubricCriterionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
  maxScore: z.number().int().min(1).max(5),
});
export type AdminRubricCriterion = z.infer<typeof adminRubricCriterionSchema>;

/** MCQ option WITH the answer key — admin/write side only (never returned publicly). */
export const adminMcqOptionSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  correct: z.boolean(),
});
export type AdminMcqOption = z.infer<typeof adminMcqOptionSchema>;

const draftRubricSchema = z.object({
  title: z.string().min(1),
  criteria: z.array(adminRubricCriterionSchema).min(1),
});

/**
 * One generated question + its rubric — the LLM output item shape AND the
 * editable draft content shape. `type`/`difficulty` are stamped by the server
 * from the generation request, so they are not part of the model's output.
 */
export const generatedQuestionSchema = z
  .object({
    prompt: z.string().min(10),
    expectedConcepts: z.array(z.string().min(1)).min(1),
    hints: z.tuple([z.string().min(1), z.string().min(1), z.string().min(1), z.string().min(1)]),
    exampleStrongAnswer: z.string().min(1),
    commonMistakes: z.array(z.string().min(1)).min(1),
    options: z.array(adminMcqOptionSchema).optional(),
    rubric: draftRubricSchema,
  })
  .refine((q) => q.options == null || q.options.length >= 2, {
    message: 'mcq questions must define at least two options',
    path: ['options'],
  })
  .refine((q) => q.options == null || q.options.some((o) => o.correct), {
    message: 'mcq questions must have at least one correct option',
    path: ['options'],
  });
export type GeneratedQuestion = z.infer<typeof generatedQuestionSchema>;

/** What we ask the LLM to return: a batch of questions. */
export const generatedBatchSchema = z.object({
  questions: z.array(generatedQuestionSchema).min(1),
});
export type GeneratedBatch = z.infer<typeof generatedBatchSchema>;

/** POST /v1/admin/questions/generate input. */
export const generateQuestionsInputSchema = z.object({
  skillId: z.string().min(1),
  type: questionTypeSchema.default('free_text'),
  difficulty: questionDifficultySchema.default('easy'),
  count: z.number().int().min(1).max(10).default(3),
  /** Specific immutable prompt-template version; defaults to the active template. */
  templateId: z.string().min(1).optional(),
});
export type GenerateQuestionsInput = z.infer<typeof generateQuestionsInputSchema>;

/**
 * GET /v1/admin/coverage-gaps query (PRE-285).
 *
 * The report itself stays whole — the ranking is computed over every cell, and
 * `totalCells` reports how many there are. Only the slice handed to the screen
 * is paged.
 */
export const coverageGapsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type CoverageGapsQuery = z.infer<typeof coverageGapsQuerySchema>;

/** POST /v1/admin/questions/backfill input (PRE-174: drive the coverage backfill). */
export const backfillCoverageInputSchema = z.object({
  /**
   * Fill only this skill's below-floor cells (PRE-284).
   *
   * The panel's generate action is "pick a skill", and everything else about the
   * run — which difficulties, which types, how many — is the coverage floor's
   * answer rather than an operator's. Omitted, the worklist stays global: that
   * is the bulk backfill, which walks the ranking instead of a chosen row.
   */
  skillId: z.string().min(1).optional(),
  /** How many below-floor cells (most-urgent first) to fill this run. */
  maxCells: z.number().int().min(1).max(20).default(5),
  /** When true, return the plan without generating (no spend). */
  dryRun: z.boolean().default(false),
});
export type BackfillCoverageInput = z.infer<typeof backfillCoverageInputSchema>;

/**
 * Statuses that content-reset is allowed to delete. BR-81: reset only ever
 * touches unpublished, LLM-generated content. `published` is deliberately NOT a
 * member — live-bank rows and anything a learner has seen are never deletable
 * this way.
 */
export const resetContentStatusSchema = z.enum(['draft', 'rejected']);
export type ResetContentStatus = z.infer<typeof resetContentStatusSchema>;

/**
 * POST /v1/admin/questions/reset input — bulk-delete generated content. Every
 * filter is optional and ANDed; omitting all of them clears ALL draft + rejected
 * questions ("reset all"). `dryRun` returns the matched count without deleting.
 */
export const resetContentInputSchema = z.object({
  /** Restrict to these statuses; omitted ⇒ both draft and rejected. */
  status: z.array(resetContentStatusSchema).nonempty().optional(),
  /** Restrict to a single skill. */
  skillId: z.string().min(1).optional(),
  /** Restrict to a single difficulty. */
  difficulty: questionDifficultySchema.optional(),
  /** Restrict to a single question type. */
  type: questionTypeSchema.optional(),
  /** When true, return the count that WOULD be deleted; delete nothing. */
  dryRun: z.boolean().default(false),
});
export type ResetContentInput = z.infer<typeof resetContentInputSchema>;

/** POST /v1/admin/questions/reset response. */
export const resetContentResultSchema = z.object({
  dryRun: z.boolean(),
  /** Rows matching the filter (for dryRun preview and post-delete confirmation). */
  matched: z.number().int(),
  /** Rows actually deleted (0 when dryRun). */
  deleted: z.number().int(),
});
export type ResetContentResult = z.infer<typeof resetContentResultSchema>;

/** Admin-facing draft/question row (includes spoiler fields). */
export const draftQuestionSchema = z.object({
  id: z.string(),
  /**
   * PRE-115: every skill this question measures. A question is no longer bound
   * to one, so this is the field to read.
   */
  skillIds: z.array(z.string()),
  /**
   * PRE-115: the first of `skillIds`, kept so the panel keeps rendering while it
   * moves to the list. Never write it — it is derived, and a question with two
   * skills has no single one.
   */
  skillId: z.string(),
  rubricId: z.string().nullable(),
  type: questionTypeSchema,
  difficulty: questionDifficultySchema,
  status: questionStatusSchema,
  source: z.string(),
  prompt: z.string(),
  expectedConcepts: z.array(z.string()),
  hints: z.array(z.string()),
  options: z.array(adminMcqOptionSchema).nullable(),
  exampleStrongAnswer: z.string(),
  commonMistakes: z.array(z.string()),
  rubric: draftRubricSchema.nullable(),
  promptTemplateId: z.string().nullable(),
  /** PRE-169: id of an existing question this draft near-duplicates; null = unique. */
  duplicateOfId: z.string().nullable(),
  /** PRE-171: advisory authoring judge score, 0..1 (best-of-3 pass fraction); null = not judged. */
  judgeScore: z.number().nullable(),
  /** PRE-171: the judge's short rationale for the score; null = not judged. */
  judgeRationale: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DraftQuestion = z.infer<typeof draftQuestionSchema>;

/** PATCH /v1/admin/questions/:id — edit a draft. All fields optional. */
export const updateDraftQuestionSchema = z
  .object({
    type: questionTypeSchema,
    difficulty: questionDifficultySchema,
    prompt: z.string().min(10),
    expectedConcepts: z.array(z.string().min(1)).min(1),
    hints: z.tuple([z.string().min(1), z.string().min(1), z.string().min(1), z.string().min(1)]),
    exampleStrongAnswer: z.string().min(1),
    commonMistakes: z.array(z.string().min(1)).min(1),
    options: z.array(adminMcqOptionSchema).nullable(),
    rubric: draftRubricSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
export type UpdateDraftQuestionInput = z.infer<typeof updateDraftQuestionSchema>;

/** GET /v1/admin/questions/drafts query. Also powers the Content browser. */
export const listDraftQuestionsQuerySchema = z.object({
  status: questionStatusSchema.default('draft'),
  skillId: z.string().min(1).optional(),
  /** Optional narrowing (Content browser). */
  type: questionTypeSchema.optional(),
  difficulty: questionDifficultySchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /**
   * PRE-285: where the page starts. Without it the bank had no second page, and
   * without the `total` the response now carries there was no way to tell a full
   * page from the end of the list — a truncated list and a complete one looked
   * identical.
   */
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListDraftQuestionsQuery = z.infer<typeof listDraftQuestionsQuerySchema>;

// ── Active LLM model (GET /v1/admin/llm-info) ───────────────────────────────

/** Which model the authoring/generation tier uses, surfaced in the admin UI. */
export const llmInfoSchema = z.object({
  authoring: z.object({
    /** Primary model id (Cloudflare Workers AI edge model). */
    primary: z.string(),
    /** Fallback model id, or null when no fallback key is configured. */
    fallback: z.string().nullable(),
  }),
  /**
   * PRE-134: false = no second provider for ANY tier, not just authoring. One
   * key configures them all, so its absence is a property of the deployment
   * rather than of the tier that happens to be on screen.
   */
  fallbackConfigured: z.boolean(),
  /** JD screenshots need the multimodal tier, which is Gemini-only. */
  visionAvailable: z.boolean(),
});
export type LlmInfo = z.infer<typeof llmInfoSchema>;

// ── AI-assisted prompt template drafting (POST /v1/admin/prompt-templates/draft)

/** A short brief the LLM expands into a full question_generation template body. */
export const draftPromptTemplateInputSchema = z.object({
  brief: z.string().min(3).max(2000),
});
export type DraftPromptTemplateInput = z.infer<typeof draftPromptTemplateInputSchema>;

export const draftPromptTemplateResultSchema = z.object({
  /** The generated template body — the operator edits it before saving a version. */
  body: z.string(),
  usage: z.object({ provider: z.string(), model: z.string(), costUsd: z.number() }),
});
export type DraftPromptTemplateResult = z.infer<typeof draftPromptTemplateResultSchema>;

// ── Content inventory (GET /v1/admin/content-summary) ───────────────────────

/** Per-(skill × status) question counts + totals for the Content overview. */
export const contentSummarySchema = z.object({
  skills: z.array(adminSkillSchema),
  counts: z.array(
    z.object({
      skillId: z.string(),
      status: questionStatusSchema,
      count: z.number().int(),
    }),
  ),
  totals: z.object({
    published: z.number().int(),
    draft: z.number().int(),
    rejected: z.number().int(),
  }),
});
export type ContentSummary = z.infer<typeof contentSummarySchema>;

// ── Roadmap explorer (GET /v1/admin/roadmaps, /v1/admin/roadmaps/:id) ────────

/** One roadmap in the admin roadmap picker. */
export const adminRoadmapSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  userId: z.string(),
  userEmail: z.string().nullable(),
  // PRE-75: true for admin-generated draft/simulation roadmaps (synthetic persona,
  // never assigned to a real learner). The explorer marks/filters these.
  isSimulation: z.boolean(),
  createdAt: z.string(),
});
export type AdminRoadmapSummary = z.infer<typeof adminRoadmapSummarySchema>;

/** A roadmap day-item ("station"): main_goal / practice / reflection. */
export const adminRoadmapItemSchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string(),
  /** The generator's detailed explanation of this station (what to do + why). */
  description: z.string(),
  dayIndex: z.number().int(),
  orderIndex: z.number().int(),
  skillId: z.string().nullable(),
  skillTitle: z.string().nullable(),
  sessionType: z.string().nullable(),
  estimatedMinutes: z.number().int().nullable(),
  status: z.string(),
});
export type AdminRoadmapItem = z.infer<typeof adminRoadmapItemSchema>;

/** One ordered question inside a roadmap-spawned practice session. */
export const adminRoadmapSessionQuestionSchema = z.object({
  questionId: z.string(),
  orderIndex: z.number().int(),
  prompt: z.string(),
  type: z.string(),
  difficulty: z.string(),
  itemStatus: z.string(),
  /** The learner's answer for this item (text or code), when they submitted one. */
  answerText: z.string().nullable(),
  answerCode: z.string().nullable(),
  /** Self-reported confidence 1..5, when captured. */
  confidence: z.number().int().nullable(),
});
export type AdminRoadmapSessionQuestion = z.infer<typeof adminRoadmapSessionQuestionSchema>;

/** A practice session spawned by (or graduating) this roadmap, with its questions. */
export const adminRoadmapSessionSchema = z.object({
  id: z.string(),
  sessionType: z.string(),
  status: z.string(),
  roadmapItemId: z.string().nullable(),
  sourceRoadmapId: z.string().nullable(),
  questions: z.array(adminRoadmapSessionQuestionSchema),
});
export type AdminRoadmapSession = z.infer<typeof adminRoadmapSessionSchema>;

/** Full roadmap structure for the explorer: roadmap → items (stations) → sessions → questions. */
export const adminRoadmapDetailSchema = z.object({
  roadmap: adminRoadmapSummarySchema.extend({
    sourceSessionId: z.string().nullable(),
    sourceRoleId: z.string().nullable(),
  }),
  items: z.array(adminRoadmapItemSchema),
  sessions: z.array(adminRoadmapSessionSchema),
});
export type AdminRoadmapDetail = z.infer<typeof adminRoadmapDetailSchema>;

// ── Raw SQL console (POST /v1/admin/db/query) ───────────────────────────────

/**
 * Input for the admin SQL console (Outerbase/LibSQL Studio embed bridge).
 * Exactly one of `statement` (single query) or `statements` (transaction batch).
 * FULL read/write incl. DDL — admin-gated only.
 */
export const dbQueryInputSchema = z
  .object({
    statement: z.string().min(1).optional(),
    statements: z.array(z.string().min(1)).min(1).optional(),
  })
  .refine((v) => Boolean(v.statement) !== Boolean(v.statements), {
    message: 'provide exactly one of statement or statements',
  });
export type DbQueryInput = z.infer<typeof dbQueryInputSchema>;

/** One result set, shaped for the LibSQL Studio iframe protocol. */
export const dbQueryResultSchema = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
  headers: z.array(
    z.object({
      name: z.string(),
      displayName: z.string(),
      originalType: z.string().nullable(),
      type: z.number(),
    }),
  ),
  stat: z.object({
    rowsAffected: z.number(),
    rowsRead: z.number().nullable(),
    rowsWritten: z.number().nullable(),
    queryDurationMs: z.number().nullable(),
  }),
  lastInsertRowid: z.number().optional(),
});
export type DbQueryResult = z.infer<typeof dbQueryResultSchema>;

// ── Prompt templates ────────────────────────────────────────────────────────

/** POST /v1/admin/prompt-templates — create the next version for a name. */
export const createPromptTemplateSchema = z.object({
  name: z.string().min(1).max(100),
  body: z.string().min(1),
});
export type CreatePromptTemplateInput = z.infer<typeof createPromptTemplateSchema>;

export const promptTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  body: z.string(),
  version: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type PromptTemplateResponse = z.infer<typeof promptTemplateSchema>;

// ── Roadmap simulation (PRE-75) ─────────────────────────────────────────────

/**
 * PRE-75: a self-declared onboarding persona that drives a simulated roadmap.
 * Mirrors the fields `RoadmapProfile` (apps/api) consumes as the cold-start prior.
 */
export const simProfileSchema = z.object({
  experienceLevel: onboardingExperienceLevelSchema,
  goal: onboardingGoalSchema,
  aiUsagePattern: z.array(onboardingAiUsagePatternSchema).min(1).max(4),
  weakAreas: z.array(onboardingWeakAreaSchema),
});
export type SimProfile = z.infer<typeof simProfileSchema>;

/** A real user shown in the simulation "copy persona from user" picker. */
export const simUserSummarySchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  // The user's onboarding persona, when they completed onboarding (else null).
  profile: simProfileSchema.nullable(),
});
export type SimUserSummary = z.infer<typeof simUserSummarySchema>;

/**
 * POST /v1/admin/sim/roadmap — run the real pipeline (persona → baseline
 * assessment with LLM-synthesized answers → real scoring → roadmap) for a
 * synthetic user, producing a draft/simulation roadmap. `sourceUserId` only
 * records which real user the persona was copied from (provenance); the server
 * uses `profile` verbatim and never touches the real user.
 */
export const simRoadmapInputSchema = z.object({
  profile: simProfileSchema,
  sourceUserId: z.string().optional(),
  // Scope the baseline questions to these skills; omitted → picked across the
  // persona's weak-area categories (falling back to any published question).
  skillIds: z.array(z.string()).optional(),
  questionCount: z.number().int().min(1).max(8).default(4),
  // Optional label appended to the roadmap title for easy identification.
  label: z.string().max(120).optional(),
  // When true, after generating the roadmap the persona also *lives through* it:
  // every practice station is answered in-character, scored, and its item
  // completed (bank-first questions). Reflection stations are marked done.
  completeRoadmap: z.boolean().default(false),
});
export type SimRoadmapInput = z.infer<typeof simRoadmapInputSchema>;

/** Aggregate scores the simulated assessment produced (drove the roadmap). */
export const simScoresSchema = z.object({
  aiDependency: z.number(),
  explanation: z.number(),
  conceptMastery: z.number(),
  weakSkillCount: z.number().int(),
  questionsAnswered: z.number().int(),
});
export type SimScores = z.infer<typeof simScoresSchema>;

/**
 * Result of a simulation run: the new (simulation) roadmap id + the synthetic
 * user it hangs off + the authoring strategy + the assessment scores. The admin
 * panel then loads the full structure via GET /v1/admin/roadmaps/:id.
 */
/** Summary of a "live through the roadmap" run (present when completeRoadmap). */
export const simCompletionSchema = z.object({
  ran: z.boolean(),
  /** Practice stations that got a real answered + scored session. */
  stationsPracticed: z.number().int(),
  /** Total questions answered across all practised stations. */
  questionsAnswered: z.number().int(),
  /** Roadmap items advanced to `completed` (practice + reflection). */
  itemsCompleted: z.number().int(),
  /** Practice stations skipped because their skill has no published questions. */
  stationsSkippedNoBank: z.number().int(),
  /** Whether every item ended completed (the roadmap fully "lived"). */
  roadmapCompleted: z.boolean(),
});
export type SimCompletion = z.infer<typeof simCompletionSchema>;

/**
 * PRE-199: what the LLM spend of a single simulation run was, rolled up from the
 * `llm_calls` ledger for the throwaway sim user. Lets an operator see the cost of
 * an end-to-end run right where they ran it (the same rows also land in the Costs
 * screen). `byPurpose` breaks the total down per workload (authoring answers,
 * scoring judge, roadmap generation …).
 */
export const simCostByPurposeSchema = z.object({
  purpose: z.string(),
  calls: z.number().int(),
  costUsd: z.number(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
});
export type SimCostByPurpose = z.infer<typeof simCostByPurposeSchema>;

export const simCostSchema = z.object({
  totalUsd: z.number(),
  calls: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  byPurpose: z.array(simCostByPurposeSchema).default([]),
});
export type SimCost = z.infer<typeof simCostSchema>;

/**
 * PRE-199: one line of the end-to-end journey the run walked, so the pipeline is
 * legible in the panel (onboarding → assessment → scoring → roadmap →
 * [completion]). `status` marks whether a phase ran, was partial, or skipped.
 */
export const simStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: z.enum(['done', 'partial', 'skipped']),
  detail: z.string().nullable().default(null),
});
export type SimStep = z.infer<typeof simStepSchema>;

export const simRoadmapResultSchema = z.object({
  roadmapId: z.string(),
  simUserId: z.string(),
  strategy: z.string(),
  itemCount: z.number().int(),
  scores: simScoresSchema,
  /** Present only when the run was asked to complete the roadmap. */
  completion: simCompletionSchema.nullable().default(null),
  /** PRE-199: LLM spend of this run (rolled up from the sim user's ledger). */
  cost: simCostSchema.nullable().default(null),
  /** PRE-199: the end-to-end steps the run walked, for observability. */
  steps: z.array(simStepSchema).default([]),
});
export type SimRoadmapResult = z.infer<typeof simRoadmapResultSchema>;

// ── Graduation / roadmap-loop simulation (PRE-198) ──────────────────────────

/**
 * PRE-198: parameters for simulating the graduation → re-loop cycle over a sim
 * roadmap. Rather than run the LLM/scoring per loop, the simulation feeds
 * synthetic per-skill mastery (derived from these knobs) into the REAL
 * graduation evaluator (BR-72) and the REAL re-loop generator (BR-56), so the
 * rules are faithful but the scenario is deterministic and tunable.
 */
export const simGraduationLoopInputSchema = z.object({
  /** The sim roadmap to start the loop from. */
  roadmapId: z.string(),
  /** Mean concept-mastery (0-100) the persona reaches on the FIRST graduation. */
  baseMastery: z.number().min(0).max(100).default(50),
  /** How much mastery rises each subsequent loop (so it eventually graduates). */
  improvementPerLoop: z.number().min(0).max(100).default(15),
  /** Safety cap on loops; if still re-looping at this point → `exhausted`. */
  maxLoops: z.number().int().min(1).max(10).default(5),
});
export type SimGraduationLoopInput = z.infer<typeof simGraduationLoopInputSchema>;

/** One target skill's graduation verdict within a loop. */
export const simGraduationSkillSchema = z.object({
  skillId: z.string(),
  title: z.string(),
  /** Synthetic mean concept-mastery fed to the evaluator this loop. */
  mastery: z.number(),
  passed: z.boolean(),
});
export type SimGraduationSkill = z.infer<typeof simGraduationSkillSchema>;

/** One iteration of the graduation loop, with the rule that fired. */
export const simLoopStepSchema = z.object({
  loop: z.number().int(),
  roadmapId: z.string(),
  threshold: z.number(),
  skills: z.array(simGraduationSkillSchema),
  outcome: z.enum(['graduated', 'reloop', 'no_targets']),
  /** Human explanation of the rule that fired (which skills passed/failed, why). */
  rule: z.string(),
  /** The re-loop roadmap generated from still-weak skills (null unless reloop). */
  reloopRoadmapId: z.string().nullable().default(null),
  /** Titles of the skills that came back into the re-loop plan. */
  reloopedSkills: z.array(z.string()).default([]),
  /** Next-step options offered on graduation (null unless graduated). */
  nextOptions: z
    .object({
      levelUpAvailable: z.boolean(),
      newTrackSlugs: z.array(z.string()),
    })
    .nullable()
    .default(null),
});
export type SimLoopStep = z.infer<typeof simLoopStepSchema>;

export const simGraduationLoopResultSchema = z.object({
  simUserId: z.string(),
  steps: z.array(simLoopStepSchema),
  finalOutcome: z.enum(['graduated', 'exhausted', 'no_targets']),
  totalLoops: z.number().int(),
  /** The loop number the persona graduated at, or null if never. */
  graduatedAtLoop: z.number().int().nullable().default(null),
});
export type SimGraduationLoopResult = z.infer<typeof simGraduationLoopResultSchema>;

/**
 * PRE-289: a launch-readiness waiver. The only writable part of the readiness
 * report, and the only part a person authors rather than the data.
 *
 * Every field is required on purpose. The shape is Google's Production Readiness
 * Review waiver — written, owned, time-boxed — and each part answers a question
 * that comes up months later: what did we know (`reason`), who agreed to it
 * (`owner`), and when does this stop being acceptable (`expiresAt`). Making any
 * of them optional turns an exception into a silent lowering of the bar.
 */
export const launchWaiverInputSchema = z.object({
  checkKey: z.enum(['skill_coverage', 'cell_completeness', 'review_integrity']),
  reason: z.string().trim().min(10).max(1000),
  owner: z.string().trim().min(1).max(200),
  /** ISO date (YYYY-MM-DD). Must be in the future — a waiver that expires today waives nothing. */
  expiresAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((d) => d > new Date().toISOString().slice(0, 10), 'Expiry must be in the future'),
});

export type LaunchWaiverInput = z.infer<typeof launchWaiverInputSchema>;


/**
 * Item models (PRE-293) — an expert writes the shape of a question, not the
 * question.
 *
 * The slot and constraint shapes are shared rather than defined per app because
 * three places have to agree on them exactly: the panel that writes a model, the
 * expansion that reads it, and the preview that shows what generation will
 * produce. A preview that disagreed with the generator by one rule would be
 * worse than no preview — it would be a promise.
 */
export const itemSlotsSchema = z.record(
  z.string().regex(/^[A-Z0-9_]+$/, 'Slot names are UPPER_SNAKE'),
  z.array(z.string().trim().min(1)).min(1),
);
export type ItemSlots = z.infer<typeof itemSlotsSchema>;

/** "When `if.slot` is `if.value`, `then.slot` may only be one of `then.oneOf`." */
export const itemConstraintSchema = z.object({
  if: z.object({ slot: z.string().min(1), value: z.string().min(1) }),
  then: z.object({ slot: z.string().min(1), oneOf: z.array(z.string().min(1)).min(1) }),
});
export type ItemConstraint = z.infer<typeof itemConstraintSchema>;

/** POST /v1/admin/item-models — write a model. */
export const itemModelInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  skillId: z.string().min(1),
  difficulty: questionDifficultySchema,
  type: questionTypeSchema,
  /** The stem, with `{SLOT}` placeholders. */
  stem: z.string().trim().min(20).max(2000),
  slots: itemSlotsSchema,
  constraints: z.array(itemConstraintSchema).default([]),
  expectedConcepts: z.array(z.string().trim().min(1)).default([]),
  /**
   * `draft` while being written, `active` once it may generate.
   *
   * A model starts as a draft even when it validates, because validation says it
   * is well-formed and nothing more — whether it measures the right thing is a
   * judgement, and the status is where somebody makes it.
   */
  status: z.enum(['draft', 'active']).default('draft'),
});
export type ItemModelInput = z.infer<typeof itemModelInputSchema>;

/** POST /v1/admin/item-models/:id/generate — how many items to draw from it. */
export const itemModelGenerateInputSchema = z.object({
  count: z.number().int().min(1).max(20).default(5),
});
export type ItemModelGenerateInput = z.infer<typeof itemModelGenerateInputSchema>;
